/*
  Quick order
  -----------
  One custom element drives the whole page.

  The catalogue tab lists every product with a quantity box. A product is part
  of the order simply by having a quantity above zero, so there is no separate
  "add to list" step — the one button adds everything and goes to checkout.

  The optional SKU tab is a set of blank rows that resolve a typed SKU against
  the same catalogue.

  State is small and flat:

    state.quantities - variant id -> quantity, for anything above zero
    state.selected   - product handle -> the variant currently chosen
    state.skuRows    - the SKU tab's rows

  The catalogue table is rebuilt only when the search, sort or filter changes.
  A quantity change patches the single row it affects, which keeps a long
  catalogue responsive and leaves focus where the shopper put it.
*/

const QUICK_ORDER_FILTER_DEBOUNCE = 200;

class QuickOrderForm extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;

    this.config = this.readConfig();
    if (!this.config) return;

    this.state = { quantities: {}, selected: {}, skuRows: [] };
    this.catalogue = [];
    this.skuIndex = new Map();
    this.filterTerm = '';
    this.sortKey = 'title';
    this.view = 'all';

    this.cacheElements();
    this.restore();

    if (!this.state.skuRows.length) {
      this.state.skuRows = this.makeSkuRows(this.config.skuRows);
    }

    this.bindTabs();
    this.bindToolbar();
    this.bindCatalogue();
    this.bindSummary();
    this.bindSku();

    this.loadCatalogue();
    this.renderSkuRows();
    this.renderSummary();
  }

  disconnectedCallback() {
    clearTimeout(this.filterTimer);
  }

  /* ---------------------------------------------------------------- setup */

  readConfig() {
    const node = this.querySelector('[data-quick-order-config]');
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('Quick order: could not read configuration.', error);
      return null;
    }
  }

  cacheElements() {
    this.tabs = Array.from(this.querySelectorAll('[data-quick-order-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-quick-order-panel]'));

    this.searchInput = this.querySelector('[data-quick-order-search-input]');
    this.searchClear = this.querySelector('[data-quick-order-search-clear]');
    this.searchSpinner = this.querySelector('[data-quick-order-search-spinner]');
    this.sortSelect = this.querySelector('[data-quick-order-sort]');
    this.chips = Array.from(this.querySelectorAll('[data-quick-order-chip]'));
    this.selectedCount = this.querySelector('[data-quick-order-selected-count]');
    this.catalogueCount = this.querySelector('[data-quick-order-catalogue-count]');

    this.rowsBody = this.querySelector('[data-quick-order-rows]');
    this.emptyMessage = this.querySelector('[data-quick-order-empty]');

    this.skuRowsBody = this.querySelector('[data-quick-order-sku-rows]');
    this.addRowsButton = this.querySelector('[data-quick-order-add-rows]');
    this.pasteInput = this.querySelector('[data-quick-order-paste]');
    this.pasteButton = this.querySelector('[data-quick-order-paste-add]');
    this.pasteResult = this.querySelector('[data-quick-order-paste-result]');

    this.badge = this.querySelector('[data-quick-order-badge]');
    this.itemCountOutput = this.querySelector('[data-quick-order-count]');
    this.subtotalOutput = this.querySelector('[data-quick-order-subtotal]');
    this.totalOutput = this.querySelector('[data-quick-order-total]');
    this.submitButton = this.querySelector('[data-quick-order-submit]');
    this.submitText = this.querySelector('[data-quick-order-submit-text]');
    this.submitSpinner = this.querySelector('.quick-order__spinner');
    this.clearButton = this.querySelector('[data-quick-order-clear]');
    this.checkoutButton = this.querySelector('[data-quick-order-checkout]');
    this.errorOutput = this.querySelector('[data-quick-order-error]');
    this.liveRegion = this.querySelector('[data-quick-order-live]');

    // Kept so the busy label can be swapped back if the add fails.
    this.submitLabel = this.submitText ? this.submitText.textContent.trim() : '';
  }

  /* Icons live in <template> tags, because the script cannot reach Liquid's
     inline_asset_content and inlining SVG source here would duplicate it. */
  icon(name) {
    const template = this.querySelector('[data-quick-order-icon="' + name + '"]');
    return template ? template.content.cloneNode(true) : document.createTextNode('');
  }

  /* ----------------------------------------------------------- persistence */

  get storageKey() {
    return this.config.storageKey + ':' + this.config.sectionId;
  }

  restore() {
    let raw = null;

    try {
      raw = window.localStorage.getItem(this.storageKey);
    } catch (error) {
      // Private browsing, or storage is blocked. The page works without it.
      return;
    }

    if (!raw) return;

    try {
      const saved = JSON.parse(raw);
      if (saved.quantities && typeof saved.quantities === 'object') this.state.quantities = saved.quantities;
      if (saved.selected && typeof saved.selected === 'object') this.state.selected = saved.selected;
      if (Array.isArray(saved.skuRows)) this.state.skuRows = saved.skuRows;

      // Errors describe the last checkout attempt, not the saved order.
      this.state.skuRows.forEach((row) => {
        row.error = null;
      });
    } catch (error) {
      this.forget();
    }
  }

  persist() {
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(this.state));
    } catch (error) {
      // Over quota or blocked: the order still works for this page view.
    }
  }

  forget() {
    try {
      window.localStorage.removeItem(this.storageKey);
    } catch (error) {
      // Nothing to do.
    }
  }

  /* ---------------------------------------------------------------- money */

  formatMoney(cents) {
    const amount = Number.isFinite(cents) ? cents : 0;

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(amount, this.config.moneyFormat);
    }

    // Only reached if global.js has not loaded; keeps the page readable.
    return (amount / 100).toFixed(2);
  }

  pluralise(count, one, many) {
    return count === 1 ? one : many.replace('[count]', String(count));
  }

  announce(message) {
    if (!this.liveRegion || !message) return;
    this.liveRegion.textContent = message;
  }

  /* ------------------------------------------------------------ catalogue */

  /*
    Page one is inlined by the section. Any further pages come from the
    Section Rendering API so the whole catalogue is filterable, but the first
    page is rendered straight away rather than waiting for them.
  */
  async loadCatalogue() {
    const node = this.querySelector('[data-quick-order-catalogue]');
    if (!node) return;

    this.catalogue = this.parseCatalogueNode(node);
    this.indexCatalogue();
    this.renderCatalogue();

    const pages = parseInt(node.dataset.pages, 10) || 1;
    const inlinePage = parseInt(node.dataset.page, 10) || 1;
    if (pages <= 1) return;

    this.setSearchLoading(true);

    for (let page = 1; page <= pages; page += 1) {
      if (page === inlinePage) continue;

      try {
        const products = await this.fetchCataloguePage(page);
        this.catalogue.push(...products);
      } catch (error) {
        console.error('Quick order: could not load catalogue page ' + page + '.', error);
      }
    }

    this.indexCatalogue();
    this.renderCatalogue();
    this.renderSummary();
    this.setSearchLoading(false);
  }

  parseCatalogueNode(node) {
    try {
      // The array is closed with a trailing null so Liquid never has to work
      // out which product is last; drop it here.
      const products = JSON.parse(node.textContent).filter(Boolean);
      products.forEach((product) => this.prepareProduct(product));
      return products;
    } catch (error) {
      console.error('Quick order: could not read the catalogue.', error);
      return [];
    }
  }

  /*
    Search text is attached as a product is parsed rather than when the SKU
    index is rebuilt, because the catalogue is filterable while later pages
    are still arriving. A product sitting in the list without it threw the
    moment anyone typed, which killed the filter outright.

    Two haystacks are kept: the text as written, and the same text reduced to
    letters and digits. The second is what lets "5htp" find "5-HTP" and
    "nv5htp120" find "NV-5HTP-120".
  */
  prepareProduct(product) {
    const terms = [product.title, product.vendor];

    product.variants.forEach((variant) => {
      terms.push(variant.title, variant.sku);
    });

    const text = terms.filter(Boolean).join(' ');
    product.searchText = text.toLowerCase();
    product.searchCompact = this.compact(text);
  }

  compact(value) {
    return String(value).toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  indexCatalogue() {
    this.skuIndex = new Map();

    this.catalogue.forEach((product) => {
      product.variants.forEach((variant) => {
        if (!variant.sku) return;

        const key = this.normaliseSku(variant.sku);
        if (this.skuIndex.has(key)) return;

        this.skuIndex.set(key, {
          sku: variant.sku,
          variant_id: variant.id,
          title: product.title,
          variant_title: variant.title,
          price: variant.price,
          available: variant.available,
          url: product.url,
          image: product.image,
        });
      });
    });

    // SKU rows resolved before the rest of the catalogue arrived may have
    // been marked not found too early.
    this.state.skuRows.forEach((row) => {
      if (!row.sku || row.match) return;

      const match = this.skuIndex.get(this.normaliseSku(row.sku));
      if (!match) return;

      row.match = match;
      row.notFound = false;
    });
  }

  async fetchCataloguePage(page) {
    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set('section_id', this.config.sectionId);
    url.searchParams.set('page', String(page));

    const response = await fetch(url.toString());
    if (!response.ok) throw new Error('Section fetch failed: ' + response.status);

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector('[data-quick-order-catalogue]');

    return node ? this.parseCatalogueNode(node) : [];
  }

  /* -------------------------------------------------- filtering + sorting */

  /*
    Every word typed has to match something on the product — its title,
    vendor, a variant name or a SKU — so "alpha 600" narrows to Alpha Lipoic
    Acid 600 mg instead of listing every Alpha product. Order does not
    matter, and a word can match a different field from the one before it.
  */
  matchesSearch(product) {
    if (!this.filterTerm) return true;

    const needles = this.filterTerm.split(/\s+/).filter(Boolean);

    return needles.every((needle) => {
      if ((product.searchText || '').includes(needle)) return true;

      const compact = this.compact(needle);
      return compact !== '' && (product.searchCompact || '').includes(compact);
    });
  }

  matchesView(product) {
    if (this.view === 'selected') {
      return product.variants.some((variant) => this.quantityFor(variant.id) > 0);
    }

    if (this.view === 'in-stock') {
      return product.variants.some((variant) => variant.available);
    }

    return true;
  }

  get visibleProducts() {
    const products = this.catalogue.filter(
      (product) => this.matchesSearch(product) && this.matchesView(product)
    );

    return this.sortProducts(products);
  }

  sortProducts(products) {
    const sorted = products.slice();

    switch (this.sortKey) {
      case 'price-asc':
        return sorted.sort((a, b) => this.variantFor(a).price - this.variantFor(b).price);
      case 'price-desc':
        return sorted.sort((a, b) => this.variantFor(b).price - this.variantFor(a).price);
      case 'sku':
        return sorted.sort((a, b) =>
          (this.variantFor(a).sku || '').localeCompare(this.variantFor(b).sku || '')
        );
      default:
        return sorted.sort((a, b) => a.title.localeCompare(b.title));
    }
  }

  variantFor(product) {
    const chosen = this.state.selected[product.handle];
    return (
      product.variants.find((variant) => variant.id === chosen) ||
      product.variants.find((variant) => variant.available) ||
      product.variants[0]
    );
  }

  quantityFor(variantId) {
    return this.state.quantities[variantId] || 0;
  }

  /* --------------------------------------------------------------- toolbar */

  bindToolbar() {
    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => {
        this.toggleSearchClear();

        clearTimeout(this.filterTimer);
        this.filterTimer = setTimeout(() => {
          this.filterTerm = this.searchInput.value.trim().toLowerCase();
          this.renderCatalogue();
        }, QUICK_ORDER_FILTER_DEBOUNCE);
      });
    }

    this.searchClear?.addEventListener('click', () => {
      this.searchInput.value = '';
      this.filterTerm = '';
      this.toggleSearchClear();
      this.renderCatalogue();
      this.searchInput.focus();
    });

    this.sortSelect?.addEventListener('change', () => {
      this.sortKey = this.sortSelect.value;
      this.renderCatalogue();
    });

    this.chips.forEach((chip) => {
      chip.addEventListener('click', () => this.setView(chip.dataset.quickOrderChip));
    });
  }

  setView(view) {
    this.view = view;

    this.chips.forEach((chip) => {
      chip.setAttribute('aria-pressed', String(chip.dataset.quickOrderChip === view));
    });

    this.renderCatalogue();
  }

  toggleSearchClear() {
    if (!this.searchClear) return;
    this.searchClear.hidden = !this.searchInput.value;
  }

  setSearchLoading(loading) {
    if (this.searchSpinner) this.searchSpinner.hidden = !loading;
  }

  /* ------------------------------------------------------- catalogue table */

  renderCatalogue() {
    if (!this.rowsBody) return;

    const products = this.visibleProducts;
    const fragment = document.createDocumentFragment();

    products.forEach((product) => fragment.append(this.buildCatalogueRow(product)));

    this.rowsBody.textContent = '';
    this.rowsBody.append(fragment);

    if (this.emptyMessage) {
      this.emptyMessage.hidden = products.length > 0 || this.catalogue.length === 0;
    }

    this.renderCounts(products.length);
  }

  renderCounts(visible) {
    const total = this.catalogue.length;

    if (this.catalogueCount) {
      if (!total) {
        this.catalogueCount.textContent = '';
      } else if (visible === total) {
        this.catalogueCount.textContent = this.pluralise(
          total,
          this.config.strings.productOne,
          this.config.strings.productMany
        );
      } else {
        this.catalogueCount.textContent = this.config.strings.productsFiltered
          .replace('[count]', String(visible))
          .replace('[total]', String(total));
      }
    }

    if (this.selectedCount) {
      const chosen = this.catalogue.filter((product) =>
        product.variants.some((variant) => this.quantityFor(variant.id) > 0)
      ).length;

      this.selectedCount.textContent = String(chosen);
    }
  }

  buildCatalogueRow(product) {
    const variant = this.variantFor(product);
    const quantity = this.quantityFor(variant.id);

    const row = document.createElement('tr');
    row.className = 'quick-order__row';
    row.dataset.handle = product.handle;
    row.dataset.variantId = String(variant.id);
    if (quantity > 0) row.classList.add('quick-order__row--active');
    if (!variant.available) row.classList.add('quick-order__row--unavailable');

    /* Product: thumbnail, title, variant picker, status */
    const productCell = document.createElement('td');
    productCell.className = 'quick-order__cell--product';

    const productWrap = document.createElement('div');
    productWrap.className = 'quick-order__product';

    if (product.image) {
      const image = document.createElement('img');
      image.className = 'quick-order__thumb';
      image.src = product.image;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      image.width = 44;
      image.height = 44;
      productWrap.append(image);
    }

    const text = document.createElement('div');
    text.className = 'quick-order__product-text';

    const title = document.createElement('a');
    title.className = 'quick-order__title';
    title.href = product.url;
    title.textContent = product.title;
    text.append(title);

    if (product.variants.length > 1) {
      const select = document.createElement('select');
      select.className = 'quick-order__variant-select';
      select.setAttribute('aria-label', 'Variant for ' + product.title);
      select.dataset.quickOrderVariant = '';

      product.variants.forEach((item) => {
        const option = document.createElement('option');
        option.value = String(item.id);
        option.textContent = item.available
          ? item.title
          : item.title + ' (' + this.config.strings.outOfStock + ')';
        option.selected = item.id === variant.id;
        select.append(option);
      });

      text.append(select);
    }

    if (!variant.available) {
      const status = document.createElement('span');
      status.className = 'quick-order__status';
      status.textContent = this.config.strings.outOfStock;
      text.append(status);
    }

    const error = document.createElement('span');
    error.className = 'quick-order__line-error';
    error.dataset.quickOrderRowError = '';
    error.setAttribute('role', 'alert');
    error.hidden = true;
    text.append(error);

    productWrap.append(text);
    productCell.append(productWrap);
    row.append(productCell);

    /* SKU */
    const skuCell = document.createElement('td');
    skuCell.className = 'quick-order__sku';
    skuCell.dataset.label = 'SKU';
    skuCell.dataset.quickOrderSkuCell = '';
    skuCell.textContent = variant.sku || '—';
    row.append(skuCell);

    /* Unit price */
    const priceCell = document.createElement('td');
    priceCell.className = 'quick-order__cell--end';
    priceCell.dataset.label = 'Unit Price';
    priceCell.dataset.quickOrderPriceCell = '';
    priceCell.textContent = this.formatMoney(variant.price);
    row.append(priceCell);

    /* Quantity */
    const qtyCell = document.createElement('td');
    qtyCell.className = 'quick-order__cell--center';
    qtyCell.dataset.label = 'Quantity';
    qtyCell.append(
      this.buildStepper({
        quantity,
        disabled: !variant.available,
        label: 'Quantity for ' + product.title,
        // Zero means "not ordered", so the catalogue's floor is 0 even when
        // the merchant has set a higher minimum per line.
        min: 0,
      })
    );
    row.append(qtyCell);

    /* Line total */
    const totalCell = document.createElement('td');
    totalCell.className = 'quick-order__cell--end quick-order__total';
    totalCell.dataset.label = 'Total';
    totalCell.dataset.quickOrderTotalCell = '';
    totalCell.textContent = this.formatMoney(variant.price * quantity);
    row.append(totalCell);

    /* Save for later */
    const actionCell = document.createElement('td');
    actionCell.className = 'quick-order__cell--action';
    actionCell.append(this.buildWishlistButton(product));
    row.append(actionCell);

    return row;
  }

  /*
    Reuses the theme's own <wish-list> element: it binds its click handler on
    connectedCallback and reads the data attributes below, so a button built
    here behaves exactly like one rendered by product-wishlist-button.liquid,
    including the header count.
  */
  buildWishlistButton(product) {
    const host = document.createElement('wish-list');

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'quick-order__icon-button wishlist-button';
    button.dataset.wishlist = '';
    button.dataset.wishlistHandle = product.handle;
    button.dataset.productId = String(product.id);
    button.title = this.config.strings.wishlistAdd;
    button.setAttribute('aria-label', 'Save ' + product.title + ' for later');

    // The theme only sets this class on click, so an item saved on an earlier
    // visit would otherwise come back looking unsaved.
    if (this.savedHandles.has(product.handle)) {
      button.classList.add('wishlist-added');
      button.title = this.config.strings.wishlistAdded;
    }

    button.append(this.icon('heart'));
    host.append(button);
    return host;
  }

  get savedHandles() {
    if (this._savedHandles) return this._savedHandles;

    let saved = [];

    try {
      saved = JSON.parse(window.localStorage.getItem('wishlistItem')) || [];
    } catch (error) {
      saved = [];
    }

    this._savedHandles = new Set(Array.isArray(saved) ? saved : []);
    return this._savedHandles;
  }

  /*
    Patches the one row a quantity or variant change affects. Rebuilding the
    table instead would be O(catalogue) per keystroke and would throw away
    focus on the control being used.
  */
  updateRow(handle) {
    const row = this.rowsBody?.querySelector('[data-handle="' + CSS.escape(handle) + '"]');
    if (!row) return;

    const product = this.catalogue.find((item) => item.handle === handle);
    if (!product) return;

    const variant = this.variantFor(product);
    const quantity = this.quantityFor(variant.id);

    row.dataset.variantId = String(variant.id);
    row.classList.toggle('quick-order__row--active', quantity > 0);
    row.classList.toggle('quick-order__row--unavailable', !variant.available);

    const skuCell = row.querySelector('[data-quick-order-sku-cell]');
    if (skuCell) skuCell.textContent = variant.sku || '—';

    const priceCell = row.querySelector('[data-quick-order-price-cell]');
    if (priceCell) priceCell.textContent = this.formatMoney(variant.price);

    const totalCell = row.querySelector('[data-quick-order-total-cell]');
    if (totalCell) totalCell.textContent = this.formatMoney(variant.price * quantity);

    const input = row.querySelector('[data-quick-order-qty]');
    if (input) {
      input.value = String(quantity);
      input.disabled = !variant.available;
    }

    const decrement = row.querySelector('[data-quick-order-step="-1"]');
    if (decrement) decrement.disabled = !variant.available || quantity <= 0;

    const increment = row.querySelector('[data-quick-order-step="1"]');
    if (increment) increment.disabled = !variant.available;
  }

  bindCatalogue() {
    if (!this.rowsBody) return;

    this.rowsBody.addEventListener('click', (event) => {
      const row = event.target.closest('[data-handle]');
      if (!row) return;

      const step = event.target.closest('[data-quick-order-step]');
      if (!step) return;

      const delta = Number(step.dataset.quickOrderStep);
      const current = this.quantityFor(Number(row.dataset.variantId));

      // The first step up lands on the merchant's minimum, and stepping back
      // below it clears the row rather than leaving an unorderable quantity.
      let next;
      if (delta > 0) {
        next = current === 0 ? this.config.minQuantity : current + 1;
      } else {
        next = current <= this.config.minQuantity ? 0 : current - 1;
      }

      this.setQuantity(row.dataset.handle, next);
    });

    this.rowsBody.addEventListener('change', (event) => {
      const row = event.target.closest('[data-handle]');
      if (!row) return;

      if (event.target.matches('[data-quick-order-qty]')) {
        const value = parseInt(event.target.value, 10);
        const next = Number.isFinite(value) && value > 0
          ? Math.max(this.config.minQuantity, value)
          : 0;

        this.setQuantity(row.dataset.handle, next);
        return;
      }

      if (event.target.matches('[data-quick-order-variant]')) {
        this.selectVariant(row.dataset.handle, Number(event.target.value));
      }
    });
  }

  setQuantity(handle, quantity) {
    const product = this.catalogue.find((item) => item.handle === handle);
    if (!product) return;

    const variant = this.variantFor(product);

    if (quantity > 0) {
      this.state.quantities[variant.id] = quantity;
    } else {
      delete this.state.quantities[variant.id];
    }

    this.setRowError(handle, null);

    // Clearing the last quantity while the Selected filter is on would leave
    // the row stranded in a list it no longer belongs to.
    if (this.view === 'selected' && quantity === 0) {
      this.renderCatalogue();
    } else {
      this.updateRow(handle);
      this.renderCounts(this.visibleProducts.length);
    }

    this.renderSummary();
    this.persist();
  }

  selectVariant(handle, variantId) {
    const product = this.catalogue.find((item) => item.handle === handle);
    if (!product) return;

    const previous = this.variantFor(product);
    const quantity = this.quantityFor(previous.id);

    this.state.selected[handle] = variantId;

    // Carry the quantity over, so switching size does not silently drop what
    // the shopper had already set.
    if (quantity > 0) {
      delete this.state.quantities[previous.id];
      this.state.quantities[variantId] = quantity;
    }

    this.updateRow(handle);
    this.renderSummary();
    this.persist();

    // The row is keyed by handle, so the select survives the patch; put focus
    // back on it because a re-render can still happen underneath.
    const row = this.rowsBody?.querySelector('[data-handle="' + CSS.escape(handle) + '"]');
    row?.querySelector('[data-quick-order-variant]')?.focus();
  }

  setRowError(handle, message) {
    const row = this.rowsBody?.querySelector('[data-handle="' + CSS.escape(handle) + '"]');
    const error = row?.querySelector('[data-quick-order-row-error]');
    if (!error) return;

    error.textContent = message || '';
    error.hidden = !message;
  }

  clearRowErrors() {
    this.rowsBody?.querySelectorAll('[data-quick-order-row-error]').forEach((error) => {
      error.textContent = '';
      error.hidden = true;
    });

    this.state.skuRows.forEach((row) => {
      row.error = null;
    });
  }

  /* --------------------------------------------------------------- steppers */

  buildStepper({ quantity, disabled, label, min, rowKey }) {
    const floor = min ?? this.config.minQuantity;

    const wrapper = document.createElement('div');
    wrapper.className = 'quick-order__stepper';

    const decrement = document.createElement('button');
    decrement.type = 'button';
    decrement.className = 'quick-order__step';
    decrement.dataset.quickOrderStep = '-1';
    if (rowKey) decrement.dataset.rowKey = rowKey;
    decrement.append(this.icon('minus'));
    decrement.setAttribute('aria-label', 'Decrease ' + label);
    decrement.disabled = disabled || quantity <= floor;

    const input = document.createElement('input');
    input.type = 'number';
    input.className = 'quick-order__qty';
    input.value = String(quantity);
    input.min = String(floor);
    input.step = '1';
    input.inputMode = 'numeric';
    input.dataset.quickOrderQty = '';
    if (rowKey) input.dataset.rowKey = rowKey;
    input.setAttribute('aria-label', label);
    input.disabled = disabled;

    const increment = document.createElement('button');
    increment.type = 'button';
    increment.className = 'quick-order__step quick-order__step--add';
    increment.dataset.quickOrderStep = '1';
    if (rowKey) increment.dataset.rowKey = rowKey;
    increment.append(this.icon('plus'));
    increment.setAttribute('aria-label', 'Increase ' + label);
    increment.disabled = disabled;

    wrapper.append(decrement, input, increment);
    return wrapper;
  }

  buildRemoveButton(label, rowKey) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'quick-order__icon-button';
    button.dataset.quickOrderRemove = '';
    if (rowKey) button.dataset.rowKey = rowKey;
    button.append(this.icon('close'));
    button.setAttribute('aria-label', label);
    return button;
  }

  /* ----------------------------------------------------------------- tabs */

  bindTabs() {
    if (!this.tabs.length) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.selectTab(tab));

      tab.addEventListener('keydown', (event) => {
        const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
        let target = null;

        if (step) {
          const index = this.tabs.indexOf(tab);
          target = this.tabs[(index + step + this.tabs.length) % this.tabs.length];
        } else if (event.key === 'Home') {
          target = this.tabs[0];
        } else if (event.key === 'End') {
          target = this.tabs[this.tabs.length - 1];
        }

        if (!target) return;

        event.preventDefault();
        this.selectTab(target);
        target.focus();
      });
    });
  }

  selectTab(tab) {
    const name = tab.dataset.quickOrderTab;

    this.tabs.forEach((item) => {
      const selected = item === tab;
      item.setAttribute('aria-selected', String(selected));
      // Only the selected tab stays in the tab order; the arrow keys move
      // between them from there.
      item.tabIndex = selected ? 0 : -1;
    });

    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.quickOrderPanel !== name;
    });
  }

  /* ------------------------------------------------------------- SKU tab */

  makeSkuRows(count) {
    // With the SKU tab switched off there is nowhere to put these, so none
    // are made and nothing empty is persisted or summed.
    if (!this.skuRowsBody) return [];
    return Array.from({ length: count }, () => this.makeSkuRow());
  }

  makeSkuRow() {
    return {
      id: 'sku-' + Math.random().toString(36).slice(2, 10),
      sku: '',
      quantity: this.config.minQuantity,
      match: null,
      notFound: false,
      error: null,
    };
  }

  normaliseSku(value) {
    return String(value).trim().toLowerCase();
  }

  bindSku() {
    if (this.addRowsButton) {
      this.addRowsButton.addEventListener('click', () => {
        this.state.skuRows.push(...this.makeSkuRows(this.config.skuRows));
        this.commitSku();
        this.announce(this.config.skuRows + ' more rows added.');
      });
    }

    if (this.pasteButton) {
      this.pasteButton.addEventListener('click', () => this.applyPaste());
    }

    if (!this.skuRowsBody) return;

    this.skuRowsBody.addEventListener('click', (event) => {
      const row = this.rowFromEvent(event);
      if (!row) return;

      if (event.target.closest('[data-quick-order-remove]')) {
        this.state.skuRows.splice(this.state.skuRows.indexOf(row), 1);

        // The tab should never be left with nothing to type into.
        if (!this.state.skuRows.length) this.state.skuRows = this.makeSkuRows(this.config.skuRows);

        this.commitSku();
        return;
      }

      const step = event.target.closest('[data-quick-order-step]');
      if (!step) return;

      row.quantity = Math.max(
        this.config.minQuantity,
        row.quantity + Number(step.dataset.quickOrderStep)
      );
      this.commitSku();
    });

    this.skuRowsBody.addEventListener('change', (event) => {
      const row = this.rowFromEvent(event);
      if (!row) return;

      if (event.target.matches('[data-quick-order-qty]')) {
        const value = parseInt(event.target.value, 10);
        row.quantity = Number.isFinite(value)
          ? Math.max(this.config.minQuantity, value)
          : this.config.minQuantity;
        this.commitSku();
      }
    });

    // focusout rather than blur, because blur does not bubble and the lookup
    // has to run however the field was left.
    this.skuRowsBody.addEventListener('focusout', (event) => {
      if (!event.target.matches('[data-quick-order-sku]')) return;

      const row = this.rowFromEvent(event);
      if (!row) return;

      const value = event.target.value.trim();

      // Tabbing through the untouched starting rows must not trigger a
      // lookup and a re-render per row.
      if (!value && !row.sku && !row.match && !row.notFound) return;

      // Leaving a field that has already been resolved is not a new lookup.
      if (value === row.sku && (row.match || row.notFound)) return;

      row.sku = value;
      this.lookupSkuRow(row);
    });
  }

  rowFromEvent(event) {
    const element = event.target.closest('[data-row-key]');
    if (!element) return null;
    return this.state.skuRows.find((row) => row.id === element.dataset.rowKey) || null;
  }

  lookupSkuRow(row) {
    if (!row.sku) {
      row.match = null;
      row.notFound = false;
      this.commitSku();
      return;
    }

    const match = this.skuIndex.get(this.normaliseSku(row.sku)) || null;

    row.match = match;
    row.notFound = !match;
    row.error = null;

    this.announce(
      match
        ? row.sku + ' matched ' + match.title + '.'
        : row.sku + ': ' + this.config.strings.skuNotFound
    );

    this.commitSku();
  }

  commitSku() {
    this.renderSkuRows();
    this.renderSummary();
    this.persist();
  }

  renderSkuRows() {
    if (!this.skuRowsBody) return;

    const focused = this.captureFocus(this.skuRowsBody);

    this.skuRowsBody.textContent = '';
    this.state.skuRows.forEach((row) => this.skuRowsBody.append(this.buildSkuRow(row)));

    this.restoreFocus(this.skuRowsBody, focused);
  }

  /*
    The SKU table is rebuilt in full because a lookup changes most of a row at
    once. That would otherwise throw focus to the body mid-interaction, since
    the lookup fires on focusout and destroys whatever was tabbed onto next,
    so the focused control is recorded and handed back.
  */
  captureFocus(container) {
    const active = document.activeElement;
    if (!active || !container.contains(active)) return null;

    const rowKey = active.dataset?.rowKey;
    if (!rowKey) return null;

    let control = null;

    if (active.matches('[data-quick-order-sku]')) {
      control = '[data-quick-order-sku]';
    } else if (active.matches('[data-quick-order-qty]')) {
      control = '[data-quick-order-qty]';
    } else if (active.matches('[data-quick-order-step]')) {
      control = '[data-quick-order-step="' + active.dataset.quickOrderStep + '"]';
    } else if (active.matches('[data-quick-order-remove]')) {
      control = '[data-quick-order-remove]';
    }

    if (!control) return null;

    return {
      rowKey,
      control,
      caret: typeof active.selectionStart === 'number' ? active.selectionStart : null,
    };
  }

  restoreFocus(container, snapshot) {
    if (!snapshot) return;

    const row = '[data-row-key="' + CSS.escape(snapshot.rowKey) + '"]';
    let target = container.querySelector(row + snapshot.control);

    // The control can come back disabled — a stepper that has just hit the
    // minimum, say. Falling back to another control in the same row keeps the
    // shopper where they were instead of at the top of the page.
    if (!target || target.disabled) {
      target = Array.from(container.querySelectorAll(row)).find((node) => !node.disabled) || null;
    }

    if (!target) return;

    target.focus();

    if (snapshot.caret !== null && typeof target.setSelectionRange === 'function') {
      try {
        target.setSelectionRange(snapshot.caret, snapshot.caret);
      } catch (error) {
        // Number inputs reject setSelectionRange in some browsers.
      }
    }
  }

  buildSkuRow(row) {
    const tr = document.createElement('tr');
    tr.className = 'quick-order__row';

    /* SKU input */
    const skuCell = document.createElement('td');
    skuCell.dataset.label = 'SKU';

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'quick-order__input';
    input.value = row.sku;
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.dataset.quickOrderSku = '';
    input.dataset.rowKey = row.id;
    input.setAttribute('aria-label', 'SKU');
    skuCell.append(input);
    tr.append(skuCell);

    /* Matched product */
    const productCell = document.createElement('td');
    productCell.className = 'quick-order__cell--product';
    productCell.dataset.label = 'Product';

    const productWrap = document.createElement('div');
    productWrap.className = 'quick-order__product-text';
    productCell.append(productWrap);

    if (row.match) {
      const title = document.createElement('a');
      title.className = 'quick-order__title';
      title.href = row.match.url;
      title.textContent =
        row.match.variant_title && row.match.variant_title !== 'Default Title'
          ? row.match.title + ' — ' + row.match.variant_title
          : row.match.title;
      productWrap.append(title);

      if (!row.match.available) {
        const status = document.createElement('span');
        status.className = 'quick-order__status';
        status.textContent = this.config.strings.outOfStock;
        productWrap.append(status);
      }
    } else if (row.notFound) {
      const notFound = document.createElement('span');
      notFound.className = 'quick-order__status quick-order__status--error';
      notFound.textContent = this.config.strings.skuNotFound;
      productWrap.append(notFound);
    }

    if (row.error) {
      const error = document.createElement('span');
      error.className = 'quick-order__line-error';
      error.setAttribute('role', 'alert');
      error.textContent = row.error;
      productWrap.append(error);
    }

    tr.append(productCell);

    /* Unit price */
    const priceCell = document.createElement('td');
    priceCell.className = 'quick-order__cell--end';
    priceCell.dataset.label = 'Unit Price';
    priceCell.textContent = row.match ? this.formatMoney(row.match.price) : '';
    tr.append(priceCell);

    /* Quantity */
    const qtyCell = document.createElement('td');
    qtyCell.className = 'quick-order__cell--center';
    qtyCell.dataset.label = 'Quantity';
    qtyCell.append(
      this.buildStepper({
        quantity: row.quantity,
        disabled: Boolean(row.match && !row.match.available),
        label: row.match ? 'Quantity for ' + row.match.title : 'Quantity',
        rowKey: row.id,
      })
    );
    tr.append(qtyCell);

    /* Line total */
    const totalCell = document.createElement('td');
    totalCell.className = 'quick-order__cell--end quick-order__total';
    totalCell.dataset.label = 'Total';
    totalCell.textContent = row.match ? this.formatMoney(row.match.price * row.quantity) : '';
    tr.append(totalCell);

    /* Remove */
    const removeCell = document.createElement('td');
    removeCell.className = 'quick-order__cell--action';
    removeCell.append(
      this.buildRemoveButton(row.sku ? 'Remove ' + row.sku : 'Remove row', row.id)
    );
    tr.append(removeCell);

    return tr;
  }

  /* ---------------------------------------------------------------- paste */

  applyPaste() {
    if (!this.pasteInput) return;

    const lines = this.pasteInput.value
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    if (!lines.length) return;

    let matched = 0;
    let unmatched = 0;

    lines.forEach((line) => {
      // "SKU, qty" or "SKU qty". A SKU can contain hyphens and dots, so the
      // quantity is only taken from a trailing run of digits.
      const parts = line.split(/[,;\t]+|\s+/).filter(Boolean);
      let sku = line;
      let quantity = this.config.minQuantity;

      if (parts.length > 1 && /^\d+$/.test(parts[parts.length - 1])) {
        quantity = Math.max(this.config.minQuantity, parseInt(parts.pop(), 10));
        sku = parts.join(' ');
      }

      sku = sku.replace(/[,;]\s*$/, '').trim();
      const match = this.skuIndex.get(this.normaliseSku(sku)) || null;

      if (match) {
        matched += 1;
      } else {
        unmatched += 1;
      }

      const target = this.state.skuRows.find((row) => !row.sku) || this.appendSkuRow();
      target.sku = sku;
      target.quantity = quantity;
      target.match = match;
      target.notFound = !match;
    });

    this.pasteInput.value = '';

    const summary = this.config.strings.pasteSummary
      .replace('[matched]', String(matched))
      .replace('[unmatched]', String(unmatched));

    if (this.pasteResult) this.pasteResult.textContent = summary;
    this.announce(summary);

    this.commitSku();
  }

  appendSkuRow() {
    const row = this.makeSkuRow();
    this.state.skuRows.push(row);
    return row;
  }

  /* -------------------------------------------------------------- summary */

  /* Everything that can actually be bought, from both tabs. */
  get orderItems() {
    const items = [];

    this.catalogue.forEach((product) => {
      product.variants.forEach((variant) => {
        const quantity = this.quantityFor(variant.id);
        if (quantity < 1 || !variant.available) return;

        items.push({
          id: variant.id,
          quantity,
          price: variant.price,
          title: product.title,
          sku: variant.sku,
          handle: product.handle,
          source: 'catalogue',
        });
      });
    });

    this.state.skuRows.forEach((row) => {
      if (!row.match?.available || row.quantity < 1) return;

      items.push({
        id: row.match.variant_id,
        quantity: row.quantity,
        price: row.match.price,
        title: row.match.title,
        sku: row.match.sku,
        source: 'sku',
        ref: row,
      });
    });

    return items;
  }

  renderSummary() {
    const items = this.orderItems;
    const count = items.reduce((total, item) => total + item.quantity, 0);
    const subtotal = items.reduce((total, item) => total + item.price * item.quantity, 0);

    if (this.itemCountOutput) this.itemCountOutput.textContent = String(count);
    if (this.subtotalOutput) this.subtotalOutput.textContent = this.formatMoney(subtotal);
    if (this.totalOutput) this.totalOutput.textContent = this.formatMoney(subtotal);
    if (this.submitButton) this.submitButton.disabled = count === 0;

    if (this.badge) {
      this.badge.textContent = this.pluralise(
        count,
        this.config.strings.itemOne,
        this.config.strings.itemMany
      );
    }
  }

  bindSummary() {
    this.submitButton?.addEventListener('click', () => this.addAllToCart());

    this.clearButton?.addEventListener('click', () => {
      this.state.quantities = {};
      this.state.skuRows = this.makeSkuRows(this.config.skuRows);
      this.forget();
      this.showError(null);
      this.announce('Order cleared.');

      this.renderCatalogue();
      this.renderSkuRows();
      this.renderSummary();
      this.persist();
    });
  }

  /* ------------------------------------------------------------ add to cart */

  async addAllToCart() {
    const items = this.orderItems;
    if (!items.length) return;

    this.clearRowErrors();
    this.showError(null);
    this.setLoading(true);

    try {
      const response = await fetch(this.config.routes.cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: items.map((item) => ({ id: item.id, quantity: item.quantity })),
        }),
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        this.handleCartError(data, items);
        this.setLoading(false);
        return;
      }

      // On success the button deliberately stays in its loading state: the
      // redirect is about to take over, and re-enabling it would invite a
      // second click that adds everything to the cart twice.
      this.handleCartSuccess(items);
    } catch (error) {
      console.error('Quick order: add to cart failed.', error);
      this.showError(this.config.strings.genericError);
      this.announce(this.config.strings.genericError);
      this.setLoading(false);
    }
  }

  handleCartSuccess(items) {
    const count = items.reduce((total, item) => total + item.quantity, 0);

    this.announce(
      this.pluralise(count, this.config.strings.addedOne, this.config.strings.addedMany)
    );

    // The order is in the cart now, so it should not be restored if the
    // shopper comes back to this page later.
    this.state.quantities = {};
    this.state.skuRows = this.makeSkuRows(this.config.skuRows);
    this.forget();

    this.goToCheckout();
  }

  /*
    Clicking the real submit button is what sends its "checkout" name along
    with the POST to the cart route, and that is what tells Shopify to go to
    checkout rather than render the cart. Submitting the form in code would
    drop the button's name and land on the cart page instead.
  */
  goToCheckout() {
    if (this.checkoutButton) {
      this.checkoutButton.click();
      return;
    }

    // The form is part of the section, so this should not happen — but the
    // cart is a better place to land than nowhere.
    window.location.href = this.config.routes.cart;
  }

  /*
    /cart/add.js rejects the whole batch on the first bad line and reports it
    as prose, with no variant id to key off. The message is matched back to a
    row by SKU and title so it can be shown in place; when nothing matches it
    still goes in the summary, so an error is never swallowed.
  */
  handleCartError(data, items) {
    const message = data?.description || data?.message || this.config.strings.genericError;
    const haystack = message.toLowerCase();

    const culprit = items.find((item) => {
      const sku = item.sku ? String(item.sku).toLowerCase() : null;
      if (sku && haystack.includes(sku)) return true;
      return item.title ? haystack.includes(item.title.toLowerCase()) : false;
    });

    if (!culprit) {
      this.showError(message);
    } else if (culprit.source === 'catalogue') {
      this.setRowError(culprit.handle, message);

      // A filtered-out row would hide the error, so make sure it is seen.
      if (!this.rowsBody?.querySelector('[data-handle="' + CSS.escape(culprit.handle) + '"]')) {
        this.showError(message);
      }
    } else {
      culprit.ref.error = message;
      this.renderSkuRows();
    }

    this.announce(message);
  }

  /*
    Deliberately not the theme's .loading class: that one blanks the button
    by making its background and text transparent, which left an empty
    outlined box. The label stays put and a spinner joins it instead.
  */
  setLoading(loading) {
    if (!this.submitButton) return;

    this.submitButton.classList.toggle('quick-order__checkout--busy', loading);
    this.submitButton.disabled = loading;
    this.submitButton.setAttribute('aria-busy', String(loading));
    this.submitSpinner?.classList.toggle('hidden', !loading);

    if (this.submitText) {
      this.submitText.textContent = loading ? this.config.strings.working : this.submitLabel;
    }

    if (!loading) this.renderSummary();
  }

  showError(message) {
    if (!this.errorOutput) return;

    this.errorOutput.textContent = message || '';
    this.errorOutput.hidden = !message;
  }
}

if (!customElements.get('quick-order-form')) {
  customElements.define('quick-order-form', QuickOrderForm);
}
